import * as z from 'zod';

import { AddressSchema } from '../shared/address.ts';
import type { ErrorBase } from '../shared/errors.ts';
import type { CountryEnum } from '../shared/geo.ts';

export interface AddressDetails {
  city: string;
  country: keyof typeof CountryEnum;
  isUndeliverable: boolean;
  postalCode?: string;
  state?: string | null;
  street: string;
  street2?: string | null;
  type: string;
}

export enum EmailDetailsType {
  NA = 'NA',
  Personal = 'Personal',
  Business = 'Business',
  Work = 'Work',
}

export interface EmailDetails {
  address: string;
  isUndeliverable: boolean;
  type: EmailDetailsType;
}

export enum PhoneDetailsType {
  Business = 'Business',
  Home = 'Home',
  Mobile = 'Mobile',
  Work = 'Work',
}

export interface PhoneDetails {
  phoneNumber: string;
  isUndeliverable: boolean;
  type: PhoneDetailsType;
}

// TODO: We need to data-shape when sending it to the browser
export interface Supporter {
  addressDetails?: AddressDetails;
  correspondenceDeliveryPreference: string;
  emailDetails?: EmailDetails;
  firstName?: string | null;
  gender?: string | null;
  globalId: string;
  internalId: string;
  lastName?: string | null;
  organizationName?: string | null;
  phoneDetails?: PhoneDetails;
  preferredName?: string | null;
  primaryContactId: string;
  primaryLanguage: string;
  rateChangeDeadline?: string | null;
  rateChangeResponse?: string;
  rateChangeSegment?: string | null;
  recordType?: string;
  salutation?: string | null;
  sourceCode?: string | null;
  status: string;
  suffix?: string | null;
  type?: string;
  typeLevel2?: string | null;
  typeLevel3?: string | null;
  errors?: ErrorBase[];
}

export type SupporterDTO = Omit<
  Supporter,
  | 'correspondenceDeliveryPreference'
  | 'gender'
  | 'rateChangeDeadline'
  | 'rateChangeResponse'
  | 'rateChangeSegment'
  | 'recordType'
  | 'status'
>;

export interface InternalSupporter {
  email?: string;
  internalId: string;
  globalId: string;
  postalCodes?: string[];
  primaryContactId: string;
}

export interface InternalContactDetails {
  contactId: string;
  email: string;
  preferences: {
    doNotContact: boolean;
    doNotEmail: boolean;
    doNotText: boolean;
    emailInterestCategories: string[];
    textInterestCategories: string[];
  };
}

export interface EmailApiSendRequest {
  messageId: string;
  emailDefinition: string;
  priority: 'immediate' | 'normal';
  to: {
    contactId: string;
    email: string;
  };
  emailAttributes: object;
}

export const SupporterPostBase = z.object({
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  address: AddressSchema,
  phone: z.string().optional(),
});

export type SupporterPostBase = z.infer<typeof SupporterPostBase>;

export const SupporterPostRequest = SupporterPostBase.extend({
  email: z.email(),
});

export type SupporterPostRequest = z.infer<typeof SupporterPostRequest>;

export type SupporterPostResponse = {
  internalId: InternalSupporter['internalId'];
};
